// ------------------------------------------------------------------------------
// name: useLoginHelper
// author: mudas
// created: 2025/4/1
// ------------------------------------------------------------------------------

import { useState } from 'react';
import Cookies from 'js-cookie';
import { get, isNil } from 'lodash-es';
import { handleLogout as cleanCookie, openURL } from 'common/utils';

import { LoginInterface } from 'common/types';
import service from 'common/service';

/**
 * 登录成功后的相关数据
 */
export interface LoginPayload {
  username: string;
  token: string;
}

// openid 登录
export const loginByOpenId = (next: string) => {
  try {
    const { origin, pathname, search, hash } = new URL(next);
    // search 中的 next 可能包含 hash 参数，但不属于 origin，origin 的 hash 参数会置于最后，两者互不干扰
    const url = `${origin}${pathname}${search}${hash}`;
    openURL(`/rbac/login?next=${encodeURIComponent(url)}`, '_self');
  } catch (error) {
    process.env.NODE_ENV === 'development' && console.warn(error);
  }
};

export const useLoginHelper = (options?: { onSuccess?: (value: LoginPayload) => void }) => {
  const { onSuccess } = options || {};
  const [loading, setLoading] = useState<boolean>();
  const [isLogin, setIsLogin] = useState(!isNil(Cookies.get('RBAC_TOKEN')));

  const setCookies = ({ username, token }: LoginPayload) => {
    Cookies.set('RBAC_TOKEN', token, { path: '/', domain: '.tiangong.yl.163.com' });
    Cookies.set('RBAC_TOKEN', token, { path: '/', domain: '.danlu.netease.com' });
    Cookies.set('RBAC_TOKEN', token, { path: '/', domain: '.fuxi.netease.com' });
    Cookies.set('RBAC_USER', username, { path: '/', domain: '.tiangong.yl.163.com' });
    Cookies.set('RBAC_USER', username, { path: '/', domain: '.danlu.netease.com' });
    Cookies.set('RBAC_USER', username, { path: '/', domain: '.fuxi.netease.com' });
  };

  const logout = () => {
    cleanCookie();
    setIsLogin(false);
  };

  // 手机号登录
  const login = async (options: LoginInterface) => {
    if (loading) return;
    setLoading(true);

    try {
      const data = await service.userSetting.login(options);
      const rbacToken = get(data, 'data.rbacToken');
      const rbacUser = get(data, 'data.rbacUser');

      if (typeof rbacToken === 'string') {
        const payload = { username: rbacUser, token: rbacToken };

        // 记录存储 cookie
        setCookies(payload);
        setIsLogin(true);

        // 成功后的回调
        onSuccess?.(payload);
      } else {
        const msg = get(data, 'message');
        return Promise.reject(msg);
      }
    } catch (error: any) {
      return Promise.reject(error.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 验证邮箱登录
  const emailLogin = async () => {
    setLoading(true);

    try {
      const data = await service.userSetting.loginByEmail();

      // rbac不会种cookie到163域名下，所以需要手动种cookie
      const rbacToken = get(data, 'data.rbacToken');
      const rbacUser = get(data, 'data.rbacUser');

      if (typeof rbacToken === 'string') {
        const payload = { username: rbacUser, token: rbacToken };

        // 记录存储 cookie
        setCookies(payload);
        setIsLogin(true);

        // 成功后的回调
        onSuccess?.(payload);
      }
    } catch (error: any) {
      return Promise.reject(error.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  return { isLogin, loading, login, logout, emailLogin };
};
